import { supabase } from '@/integrations/supabase/client';
import { validateUserData, resetUserStatsToMatchActualData } from './dataValidation';

export interface DataSyncResult {
  success: boolean;
  message: string;
  fixedIssues: string[];
  errors: string[];
}

/**
 * Comprehensive data synchronization utility
 * Validates user data and attempts to fix inconsistencies
 */
export async function syncUserData(userId: string): Promise<DataSyncResult> {
  const result: DataSyncResult = {
    success: false,
    message: '',
    fixedIssues: [],
    errors: []
  };

  try {
    console.log('🔄 Starting data sync for user:', userId);

    // Step 1: Validate current data
    const validation = await validateUserData(userId);
    
    if (validation.isValid) {
      result.success = true;
      result.message = 'Data is already synchronized';
      return result;
    }

    console.log('❌ Data validation failed, attempting fixes...');
    console.log('Issues found:', validation.issues);

    // Step 2: Reset user stats to match actual data
    try {
      await resetUserStatsToMatchActualData(userId);
      result.fixedIssues.push('User stats reset to match actual data');
    } catch (error) {
      console.error('❌ Failed to reset user stats:', error);
      result.errors.push('Failed to reset user stats');
    }

    // Step 3: Re-validate after fixes
    const postFixValidation = await validateUserData(userId);
    
    if (postFixValidation.isValid) {
      result.success = true;
      result.message = 'Data successfully synchronized';
    } else {
      result.success = false;
      result.message = 'Some issues remain after sync attempt';
      result.errors.push(...postFixValidation.issues);
    }

    console.log('🔄 Data sync completed:', result);
    return result;

  } catch (error) {
    console.error('❌ Data sync failed:', error);
    result.success = false;
    result.message = 'Data sync failed due to unexpected error';
    result.errors.push(error instanceof Error ? error.message : 'Unknown error');
    return result;
  }
}

/**
 * Force sync all user data by reloading from database
 * This is a more aggressive approach that refreshes the entire state
 */
export async function forceRefreshUserData(userId: string) {
  try {
    console.log('🔄 Force refreshing user data...');
    
    // Clear any cached data (if your app uses caching)
    localStorage.removeItem(`user_state_${userId}`);
    
    // Force reload the page to ensure fresh data load
    window.location.reload();
    
  } catch (error) {
    console.error('❌ Failed to force refresh user data:', error);
    throw error;
  }
}